import useSWR from 'swr';

import { apiFetcher } from '../utils/fetchers';
import { getPlayerState } from '../services/players';
import { PlayerContextState } from '../contexts/player';

export const useGetPlayerStateByPlayerCode = (
  gameCode?: string,
  playerCode?: string
) => {
  const { data, error, isLoading } = useSWR<PlayerContextState>(
    gameCode && playerCode
      ? `games/${gameCode}/players/${playerCode}/states`
      : null,
    apiFetcher,
    {
      revalidateOnFocus: true
    }
  );

  return { data, error, isLoading };
};

async function fetchAllPlayerStates(
  _key: string,
  gameCode: string,
  playerCodes: string[]
): Promise<PlayerContextState[]> {
  const results = await Promise.allSettled(
    playerCodes.map((code) => getPlayerState(gameCode, code))
  );

  return results
    .filter(
      (result): result is PromiseFulfilledResult<PlayerContextState> =>
        result.status === 'fulfilled'
    )
    .map((result) => result.value);
}

export function useGetAllPlayerStates(
  gameCode: string | undefined,
  players: { code?: string }[] | undefined
) {
  const playerCodes = players?.map((p) => p.code).filter(Boolean) as string[];

  const shouldFetch = !!gameCode && playerCodes?.length > 0;

  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? ['player-states', gameCode, playerCodes] : null,
    ([key, code, codes]) => fetchAllPlayerStates(key, code, codes),
    {
      revalidateOnReconnect: true,
      dedupingInterval: 5000
    }
  );

  return {
    playerStates: data,
    isLoading,
    isError: !!error,
    refresh: mutate
  };
}
